import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum, IFile, SVGFC } from '@repo/toolkit/types'
import React, { useState } from 'react'

import { ImageIcon, UploadIcon, VideoIcon } from '../../../icons'
import UploadFileInput, { FileInputOptions } from '../../atoms/input/file/file-input'
import Typography from '../../atoms/typography'
import LineTab from '../tabs'
import { fileSelectionLocaleMapping } from './locale'

type FileTypeMappingType = {
  type: FileTypeEnum
  tab: string
  label: string
  icon: SVGFC
  allowedExtensions: string[]
}

type Props = {
  files: IFile[]
  title?: string
  locale?: AvailableLanguages
  required?: boolean
  options?: FileInputOptions
  hasError?: boolean
  onFilesChange: (files: IFile[]) => void
}

const FilesSelection: React.FC<Props> = ({
  files,
  title,
  locale = 'fr',
  options,
  hasError,
  onFilesChange,
  required
}) => {
  const [uploadFilesType, setUploadFilesType] = useState<FileTypeEnum>(FileTypeEnum.IMAGE)
  const wording = fileSelectionLocaleMapping[locale]

  const fileTypeMapping: FileTypeMappingType[] = [
    {
      type: FileTypeEnum.IMAGE,
      tab: wording.photosLabel,
      label: wording.photosAction,
      icon: ImageIcon,
      allowedExtensions: ['jpg', 'jpeg', 'png', 'webp']
    },
    {
      type: FileTypeEnum.VIDEO,
      tab: wording.videosLabel,
      label: wording.videosAction,
      icon: VideoIcon,
      allowedExtensions: ['mp4', 'MP4']
    },
    {
      type: FileTypeEnum.PDF,
      tab: wording.documentsLabel,
      label: wording.documentsAction,
      icon: UploadIcon,
      allowedExtensions: ['pdf']
    }
  ]

  const selectedFileType = fileTypeMapping.find((fileType) => fileType.type === uploadFilesType)

  return (
    <div className="rounded-xl border border-grey-light flex flex-col gap-3">
      <Typography variant="text" color={tailwindColors.primary.DEFAULT} className="m-4">
        {title ?? wording.files}
      </Typography>

      <LineTab
        tabClassName="flex-1"
        tabTextVariant="tag"
        activeTabColor={tailwindColors.black.DEFAULT}
        activeTab={fileTypeMapping.findIndex((fileType) => fileType.type === uploadFilesType)}
        onTabChange={(index) => setUploadFilesType(fileTypeMapping[index].type)}
        tabs={fileTypeMapping.map((fileType) => ({ label: fileType.tab }))}
      />

      <div className="p-4">
        <UploadFileInput
          multiple
          previewOnClick
          options={options}
          hasError={hasError}
          allowedExtensions={selectedFileType?.allowedExtensions}
          placeholder={wording.dragAndDrop}
          types={[uploadFilesType]}
          icon={selectedFileType?.icon}
          label={StringFormatter.isRequired(selectedFileType?.label ?? '', required)}
          files={files.filter((file) => file.type === uploadFilesType)}
          onFilesChange={(newfiles) =>
            onFilesChange([...files.filter((file) => file.type !== uploadFilesType), ...newfiles])
          }
          onFileError={(err) => console.error('File upload error:', err)}
        />
      </div>
    </div>
  )
}

export default FilesSelection
